import styled from 'styled-components';
import { Header2, Header3, Paragraph } from '@ardoq/typography';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { PresetIcon, TextInput } from '@ardoq/forms';
import { colors, s32 } from '@ardoq/design-tokens';
import { TertiaryButton, SegmentedControl } from '@ardoq/button';
import { FieldsTable } from './FieldsTable';
import { Datasource, Filter, Popovers, Texts } from './types';
import { getDefaultTexts } from './utils';
import { type I18nLocale, t } from '@ardoq/i18n';
import {
  PopoverContentType,
  WithPopoverPlacement,
  WithPopover,
} from '@ardoq/popovers';
import { FlexBox, Stack } from '@ardoq/layout';
import { useMemo, useState } from 'react';
import {
  EmptyState,
  IconBadge,
  StatusType,
  EmptyStateToAddIllustration,
} from '@ardoq/status-ui';
import { debounce } from 'lodash/fp';

type FieldSelectorProps<T extends object> = {
  i18nLocale: I18nLocale;
  fields: T[];
  tableName: string | null;
  selectedFields: string[];
  requiredFieldsPopoverContent?: Record<string, PopoverContentType>;
  onClickRemoveAll: () => void;
  onClickAddAll: (() => void) | null;
  onClickRemove: (field: string) => void;
  onClickAdd: (field: string) => void;
  isLoading: boolean;
  queryButton?: React.ReactNode;
  settingsButton?: React.ReactNode;
  texts?: Texts;
  popovers?: Popovers;
  // TODO: This is a known house-keeping fix.
  // Adding this in-favor to unblock the Azure Migration which only has the id column
  visibleNameColumn?: boolean;
  highlight?: boolean;
  searchLayout?: 'column' | 'row';
  selectFieldsLabel?: string;
  selectedCountFallback?: string;
  allFilterLabel?: string;
  addedFilterLabel?: string;
  noResultsTitle?: string;
  noResultsDescription?: string;
};

type DefaultQueryFilterButtonProps = {
  label: string;
  isDisabled?: boolean;
  isActive?: boolean;
  onClick: VoidFunction;
};
export const DefaultQueryFilterButton = ({
  label,
  isDisabled,
  isActive,
  onClick,
}: DefaultQueryFilterButtonProps) => {
  return (
    <QueryFilterButton onClick={onClick} isDisabled={isDisabled}>
      {isActive ? (
        <IconBadge
          iconName={IconName.FILTER_LIST}
          iconSize={IconSize.LARGE}
          statusType={StatusType.NEW}
        />
      ) : (
        <PresetIcon
          iconName={IconName.FILTER_LIST}
          iconSize={IconSize.LARGE}
          color={colors.icon}
        />
      )}
      {label}
    </QueryFilterButton>
  );
};

const SettingsButton = styled(TertiaryButton)`
  position: relative;
`;

type DefaultSettingsButtonProps = {
  i18nLocale: I18nLocale;
  onClick: VoidFunction;
  hasSettings: boolean;
  settingsLabel?: string;
};

export const DefaultSettingsButton = ({
  i18nLocale,
  onClick,
  hasSettings,
  settingsLabel = t(
    i18nLocale,
    'integrations:fieldSelector.settingsLabel',
    'Settings'
  ),
}: DefaultSettingsButtonProps) => {
  return (
    <SettingsButton onClick={onClick}>
      {hasSettings ? (
        <IconBadge
          iconName={IconName.SETTINGS}
          iconSize={IconSize.LARGE}
          statusType={StatusType.NEW}
        />
      ) : (
        <PresetIcon
          iconName={IconName.SETTINGS}
          iconSize={IconSize.LARGE}
          color={colors.icon}
        />
      )}
      {settingsLabel}
    </SettingsButton>
  );
};

export const FieldSelector = <T extends Datasource>({
  i18nLocale,
  fields,
  tableName,
  onClickRemoveAll,
  onClickAddAll,
  onClickRemove,
  onClickAdd,
  isLoading = false,
  queryButton,
  settingsButton,
  texts = getDefaultTexts(i18nLocale),
  visibleNameColumn = true,
  popovers,
  selectedFields,
  highlight,
  searchLayout = 'column',
  requiredFieldsPopoverContent = {},
  selectFieldsLabel,
  selectedCountFallback = t(
    i18nLocale,
    'integrations:fieldSelector.selectedCountFallback',
    'of all fields selected'
  ),
  allFilterLabel = t(
    i18nLocale,
    'integrations:fieldSelector.allFilterLabel',
    'All'
  ),
  addedFilterLabel = t(
    i18nLocale,
    'integrations:fieldSelector.addedFilterLabel',
    'Added'
  ),
  noResultsTitle,
  noResultsDescription,
}: FieldSelectorProps<T>) => {
  const [filter, setFilter] = useState<Filter>(Filter.ALL);
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>(search);

  const filteredFields = useMemo(() => {
    if (filter === Filter.ADDED) {
      return fields.filter(field => selectedFields.includes(field.id));
    }
    return fields;
  }, [fields, filter, selectedFields]);

  const searchedFields = useMemo(() => {
    return filteredFields.filter(
      field =>
        field.name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        field.label.toLowerCase().includes(debouncedSearch.toLowerCase())
    );
  }, [debouncedSearch, filteredFields]);

  const handleDebounceSearch = useMemo(
    () =>
      debounce(200, (value: string) => {
        setDebouncedSearch(value);
      }),
    []
  );

  const handleOnSearch = (value: string) => {
    setSearch(value);
    handleDebounceSearch(value);
  };

  const resolvedSelectFieldsLabel =
    selectFieldsLabel ??
    t(
      i18nLocale,
      'integrations:fieldSelector.selectFieldsForTable',
      { tableName: tableName ?? '' },
      'Select fields for table: {{tableName}}'
    );

  if (!tableName) {
    return (
      <>
        <FlexBox
          justify="center"
          gap="medium"
          align="center"
          flex={1}
          width="full"
        >
          <EmptyState
            media={<EmptyStateToAddIllustration />}
            title={texts.noTableTitle}
            description={texts.noTableSubtitle}
            layout="vertical"
          />
        </FlexBox>
      </>
    );
  }

  return (
    <FieldSelectorContainer $highlight={highlight}>
      {!isLoading && fields.length === 0 ? (
        <Header3 color="textSubtle">{texts.noAvailableTable}</Header3>
      ) : (
        <>
          <FlexBox justify="space-between" gap="medium" align="center">
            <FlexBox gap="small" align="center">
              <Header2>
                {texts.subtitle}
                {!texts.subtitle && resolvedSelectFieldsLabel}
              </Header2>
              {popovers?.subtitlePopover && (
                <WithPopover
                  content={popovers?.subtitlePopover}
                  preferredPlacement={WithPopoverPlacement.TOP}
                >
                  <Icon iconName={IconName.HELP} color={colors.icon}></Icon>
                </WithPopover>
              )}
            </FlexBox>
          </FlexBox>
          <OpacityContainer gap="medium" $isTableSelected={Boolean(tableName)}>
            <FlexBox
              marginBottom="medium"
              justify={'space-between'}
              align={searchLayout === 'row' ? 'center' : undefined}
              gap="medium"
              flexDirection={searchLayout}
            >
              <FlexBox gap={'medium'}>
                <Stack flex={1}>
                  <TextInput
                    leftIconName={IconName.SEARCH}
                    value={search}
                    onValueChange={handleOnSearch}
                    onClear={() => handleOnSearch('')}
                    placeholder={texts.searchPlaceholder}
                    helperText={texts.searchHelperText}
                  />
                </Stack>
                {queryButton}
                {settingsButton}
              </FlexBox>
              <FlexBox justify="space-between" align="center" gap="medium">
                <Paragraph variant="caption">
                  {selectedFields.length} / {fields.length}{' '}
                  {texts.selectedCount ?? selectedCountFallback}
                </Paragraph>
                <FilterContainer>
                  <SegmentedControl
                    items={[
                      {
                        id: Filter.ALL,
                        label: allFilterLabel,
                        icon: IconName.TABLE_ROWS,
                      },
                      {
                        id: Filter.ADDED,
                        label: addedFilterLabel,
                        icon: IconName.CHECKBOX_CHECKED,
                      },
                    ]}
                    selectedId={filter}
                    onSelectionChange={setFilter}
                  />
                </FilterContainer>
              </FlexBox>
            </FlexBox>
          </OpacityContainer>

          <FieldsTable
            i18nLocale={i18nLocale}
            fields={searchedFields}
            onRemoveAll={onClickRemoveAll}
            onAddAll={onClickAddAll}
            labelColumnTitle={texts.labelColumnTitle}
            idColumnTitle={texts.idColumnTitle}
            visibleNameColumn={visibleNameColumn}
            isLoading={isLoading}
            noResultsTitle={noResultsTitle}
            noResultsDescription={noResultsDescription}
            // Using set instead of array for selectedFields so that
            // when rendering a row, we need a conditional render to
            // check if the field is selected or not
            // instead of using array.includes which is O(n)
            // and can cause performance issues, we are using set.has
            // which is O(1)
            selectedFields={new Set(selectedFields)}
            onFieldClick={(field: string) =>
              selectedFields.includes(field)
                ? onClickRemove(field)
                : onClickAdd(field)
            }
            requiredFieldsPopoverContent={requiredFieldsPopoverContent}
          />

          {!tableName && (
            <NoTableSelected>
              <Header2>{texts.noTableTitle}</Header2>
              <Paragraph variant="text1" color="textSubtle">
                {texts.noTableSubtitle}
              </Paragraph>
            </NoTableSelected>
          )}
        </>
      )}
    </FieldSelectorContainer>
  );
};

const NoTableSelected = styled(Stack).attrs({
  align: 'center',
})`
  top: 50%;
  margin-left: auto;
  margin-right: auto;
  left: 0;
  right: 0;
  position: absolute;
`;

const OpacityContainer = styled(Stack)<{ $isTableSelected: boolean }>`
  opacity: ${({ $isTableSelected }) => ($isTableSelected ? 1 : 0.5)};
`;

const QueryFilterButton = styled(TertiaryButton)`
  position: relative;
`;

const FieldSelectorContainer = styled(Stack).attrs({
  gap: 'medium',
})<{ $highlight?: boolean }>`
  border-radius: 8px;
  flex: 1;
  min-width: 528px;
  min-height: 600px;
  position: relative;
  ${({ $highlight: highlight }) =>
    highlight
      ? `
      background-color: ${colors.surfaceDefaultSubtle};
      padding: ${s32} ${s32} 0 ${s32};
      td, th {
        background-color: ${colors.surfaceDefaultSubtle};
      }
      `
      : ''};
`;

const FilterContainer = styled.div`
  width: 208px;
`;
